/**
 * "How the pot grows" flywheel explainer — a large DOM modal that teaches the
 * core economic loop in three glances:
 *
 *   1. You TOUCH the throne  → your fee drops into the pot, you're king.
 *   2. The token gets hot    → touches draw eyes, buyers pile in, price climbs.
 *   3. Trades feed the pot    → every buy/sell pays a fee on-chain, a slice of
 *                               which flows straight into the prize pool.
 *
 * …and it loops: bigger pot → more challengers → more trades → bigger pot.
 *
 * This is the player-facing companion to the diegetic <FlywheelBoard> wall
 * plaque. The plaque is ambient lore; this modal is the "read me first" pitch.
 * Source of truth for the economics: docs/koth-pumpfun-design.md §§ 2 & 9.
 *
 * <FlywheelHelp/> is self-contained: it renders a top-left "How the pot grows"
 * pill, auto-opens once per browser on first visit (localStorage gate), and
 * frees the pointer-lock cursor while open. Mount it once in the Lobby.
 */
import React, { useEffect, useState } from "react";

import { useIsTouch } from "../../util/useIsTouch";

interface FlywheelStep {
  icon: string;
  iconBg: string;
  iconRing: string;
  title: string;
  body: string;
}

const STEPS: FlywheelStep[] = [
  {
    icon: "👑",
    iconBg: "#2a1f10",
    iconRing: "rgba(244, 211, 106, 0.35)",
    title: "You touch the throne",
    body:
      "You become king and your fee drops straight into the prize pool. The timer resets — last one holding the throne when it hits zero takes the biggest cut.",
  },
  {
    icon: "📈",
    iconBg: "#101f2a",
    iconRing: "rgba(122, 196, 244, 0.35)",
    title: "The token heats up",
    body:
      "Every touch is a live event. Eyes hit the chart, buyers pile in, and the token's price climbs.",
  },
  {
    icon: "💰",
    iconBg: "#0a1f14",
    iconRing: "rgba(127, 230, 168, 0.4)",
    title: "Trading fees feed the pot",
    body:
      "Every buy and every sell pays a fee on-chain — and a slice of each trade flows straight into the prize pool. Automatically. No middleman.",
  },
];

export function FlywheelHelp(): JSX.Element {
  const isTouch = useIsTouch();
  // Auto-open on every lobby load so the loop is the first thing players read.
  // Dismissal is per-load only (no persistence) — a reload pops it again, and
  // the top-left pill reopens it any time within a session.
  const [open, setOpen] = useState(true);

  // Free the cursor while the modal is up (the lobby holds pointer lock).
  useEffect(() => {
    if (open && document.pointerLockElement) document.exitPointerLock?.();
  }, [open]);

  const close = (): void => setOpen(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={
          isTouch
            ? { ...triggerStyle, transform: "scale(0.78)", transformOrigin: "left top" }
            : triggerStyle
        }
        title="How the prize pool grows"
      >
        <span style={{ fontSize: 14 }}>↻</span> How the pot grows
      </button>
      {open && <FlywheelModal onClose={close} />}
    </>
  );
}

function FlywheelModal({ onClose }: { onClose: () => void }): JSX.Element {
  // Esc to dismiss.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="How the prize pool grows"
      onClick={onClose}
      style={backdropStyle}
    >
      <style>{KEYFRAMES}</style>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <button onClick={onClose} style={closeBtnStyle} aria-label="Close">
          ×
        </button>

        <div style={kickerStyle}>
          <span className="flywheel-spin-icon" style={spinnerStyle} aria-hidden>
            ↻
          </span>
          THE FLYWHEEL
        </div>
        <h2 style={titleStyle}>Every touch grows the pot</h2>
        <p style={subtitleStyle}>
          The prize pool isn't capped to entry fees — here's the loop that fills
          it.
        </p>

        <div style={stepsWrapStyle}>
          {STEPS.map((step, i) => (
            <React.Fragment key={step.title}>
              <Step step={step} index={i + 1} />
              {i < STEPS.length - 1 && (
                <div style={arrowStyle} aria-hidden>
                  ↓
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={loopBannerStyle}>
          <span style={{ fontSize: 18 }} aria-hidden>
            ↻
          </span>
          <span>
            <strong style={{ color: "#7fe6a8" }}>It compounds.</strong> Bigger
            pot → more challengers → more trades → bigger pot.
          </span>
        </div>

        <div style={tldrStyle}>
          <span>
            <strong style={{ color: "#f4d36a" }}>Touch</strong> = attention =
            price up.
          </span>
          <span style={{ color: "#3a3a48" }}>•</span>
          <span>
            <strong style={{ color: "#7fe6a8" }}>Trades</strong> = fees = pot
            up.
          </span>
        </div>

        <button onClick={onClose} style={ctaStyle}>
          Got it — let's play
        </button>
      </div>
    </div>
  );
}

function Step({ step, index }: { step: FlywheelStep; index: number }): JSX.Element {
  return (
    <div style={stepRowStyle}>
      <div
        style={{
          ...stepIconStyle,
          background: step.iconBg,
          boxShadow: `0 0 0 2px ${step.iconRing}`,
        }}
      >
        <span aria-hidden>{step.icon}</span>
        <span style={stepNumStyle}>{index}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={stepTitleStyle}>{step.title}</div>
        <div style={stepBodyStyle}>{step.body}</div>
      </div>
    </div>
  );
}

const KEYFRAMES = `
@keyframes flywheel-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes flywheel-pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .flywheel-spin-icon { animation: none !important; }
}
`;

const triggerStyle: React.CSSProperties = {
  position: "absolute",
  // Below the TokenMcapPanel (top:12, left:12, width:240, z-index 9). That panel
  // is variable-height — it carries a progress bar + "to next payout" row (~115px)
  // and grows another ~60px when the "recent crossings" log populates. top:138
  // clears the common (no-log) height; zIndex:10 keeps this pill ABOVE the panel
  // so even the tall log state can never hide it again.
  top: 138,
  left: 12,
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 12px",
  background: "rgba(20,20,30,0.85)",
  border: "1px solid #2f5a3f",
  borderRadius: 999,
  color: "#cdeed8",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.2,
  cursor: "pointer",
  zIndex: 10,
  pointerEvents: "auto",
  boxShadow: "0 0 0 1px rgba(127, 230, 168, 0.08)",
};

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(2, 6, 4, 0.72)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 16,
  zIndex: 1500,
  pointerEvents: "auto",
};

const cardStyle: React.CSSProperties = {
  position: "relative",
  width: "min(560px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "linear-gradient(180deg, #0e1814 0%, #0b0d14 60%)",
  border: "1px solid #244233",
  borderRadius: 16,
  padding: "28px 28px 24px",
  color: "#e8e8ef",
  boxShadow:
    "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(127, 230, 168, 0.08)",
  animation: "flywheel-pop 0.22s ease-out",
};

const closeBtnStyle: React.CSSProperties = {
  position: "absolute",
  top: 14,
  right: 16,
  background: "transparent",
  border: "none",
  color: "#9aa0b4",
  fontSize: 26,
  lineHeight: 1,
  cursor: "pointer",
};

const kickerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 2,
  color: "#7fe6a8",
  textTransform: "uppercase",
};

const spinnerStyle: React.CSSProperties = {
  display: "inline-block",
  fontSize: 15,
  animation: "flywheel-spin 4s linear infinite",
};

const titleStyle: React.CSSProperties = {
  margin: "10px 0 6px",
  fontSize: 28,
  fontWeight: 800,
  lineHeight: 1.1,
  letterSpacing: -0.5,
};

const subtitleStyle: React.CSSProperties = {
  margin: "0 0 22px",
  fontSize: 14,
  color: "#9aa0b4",
  lineHeight: 1.45,
};

const stepsWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const stepRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
  padding: "14px 16px",
  background: "rgba(255,255,255,0.02)",
  border: "1px solid #1d2a22",
  borderRadius: 12,
};

const stepIconStyle: React.CSSProperties = {
  position: "relative",
  flexShrink: 0,
  width: 52,
  height: 52,
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 26,
};

const stepNumStyle: React.CSSProperties = {
  position: "absolute",
  top: -6,
  left: -6,
  width: 20,
  height: 20,
  borderRadius: 999,
  background: "#7fe6a8",
  color: "#06140d",
  fontSize: 11,
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const stepTitleStyle: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  marginBottom: 3,
};

const stepBodyStyle: React.CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.5,
  color: "#b9c0cf",
};

const arrowStyle: React.CSSProperties = {
  textAlign: "center",
  color: "#4a8e6a",
  fontSize: 18,
  lineHeight: 1,
  margin: "2px 0",
};

const loopBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 18,
  padding: "12px 16px",
  background: "rgba(127, 230, 168, 0.08)",
  border: "1px solid #2f5a3f",
  borderRadius: 12,
  fontSize: 13.5,
  lineHeight: 1.45,
  color: "#dfe6ee",
};

const tldrStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  marginTop: 16,
  fontSize: 13.5,
  color: "#c4ccd8",
};

const ctaStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 22,
  padding: "13px 18px",
  background: "#1ea95f",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
};
